import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region components
import useIncomingInvoiceService from "serviceHooks/useIncomingInvoiceService";
import useMountedState from "./useMountedState";
//#endregion

/**
 * Resolves the company-specific supplier-invoice email address for the active company,
 * or `undefined` when the API returns none or the address can't be read.
 *
 * Best-effort and shared by the supplier-invoice upload banner and the incoming-invoices
 * report header, so both surfaces stay in sync. Re-reads on company switch.
 */
const useCompanyInvoiceMail = (): string | undefined => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const selectedCompanyId = useSelector((state: AppState) => state.auth.selectedCompanyId);
  const { getMounted } = useMountedState();
  const [companyInvoiceMail, setCompanyInvoiceMail] = React.useState<string>();

  React.useEffect(() => {
    setCompanyInvoiceMail(undefined);
    if (!selectedCompanyId) {
      return;
    }
    let cancelled = false;
    incomingInvoiceService
      .getCompanyInvoiceMail()
      .then((invoiceMailSettings) => {
        if (!cancelled && getMounted() && invoiceMailSettings?.companySpecificInvoiceMail) {
          setCompanyInvoiceMail(invoiceMailSettings.companySpecificInvoiceMail);
        }
      })
      .catch(() => {
        // Best-effort read (showErrors:false, so no toast); leave the address unset.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  return companyInvoiceMail;
};

export default useCompanyInvoiceMail;
